import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { SignOut } from '@phosphor-icons/react';
import logo from '../imgs/Logo_Fcn.png';

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol-400/70';

const toTitleCase = (text = '') => text.toLowerCase().replace(/(^|\s)\S/g, (char) => char.toUpperCase());

const getFirstName = (user) =>
  toTitleCase((user?.empleado?.NombreEmpleado || user?.usuario || '').trim().split(/\s+/)[0]);

// loginTime se guarda en localStorage al iniciar sesión, pero solo llega al estado de "user" tras recargar
const getLoginTime = (user) => {
  if (Number(user?.loginTime) > 0) return Number(user.loginTime);
  try {
    const saved = JSON.parse(localStorage.getItem('user'));
    return Number(saved?.loginTime) > 0 ? Number(saved.loginTime) : null;
  } catch (error) {
    return null;
  }
};

const formatDuration = (ms) => {
  const minutes = Math.floor(Math.max(0, ms) / 60000);
  if (minutes < 1) return 'Menos de 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

const formatStart = (time) => {
  const date = new Date(time);
  const clock = date.toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit', hour12: true });
  if (date.toDateString() === new Date().toDateString()) return clock;
  const day = date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
  return `${day}, ${clock}`;
};

const LogoutModal = ({ show, onClose, onConfirm, user }) => {
  const [loading, setLoading] = useState(false);
  const confirmButtonRef = useRef(null);

  // PortalMain se vuelve a renderizar cada segundo (reloj) y pasa funciones nuevas cada vez:
  // se guardan en referencias para que el efecto no se reinicie ni le quite el foco al usuario.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  useEffect(() => {
    if (!show) return;

    setLoading(false);
    confirmButtonRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !loadingRef.current) onCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  if (!show) return null;

  // El cierre en el servidor puede tardar: el modal se queda abierto con un estado de progreso
  // hasta que la sesión se cierra (en ese momento el portal se desmonta).
  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  const firstName = getFirstName(user);
  const loginTime = getLoginTime(user);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 animate-fade-in bg-cafe-800/30 backdrop-blur-[2px]"
        onClick={loading ? undefined : onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-title"
        aria-busy={loading}
        className="relative w-full max-w-md animate-card-in rounded-2xl bg-white p-6 text-center shadow-[0_30px_80px_-30px_rgba(59,39,25,0.55)] sm:p-8"
      >
        <button
          onClick={onClose}
          disabled={loading}
          title="Cerrar"
          aria-label="Cancelar y cerrar"
          className={`absolute right-3 top-3 rounded-lg p-2 text-neutral-400 transition-colors duration-200 hover:bg-cafe-100/50 hover:text-cafe-700 disabled:cursor-not-allowed disabled:opacity-40 ${FOCUS}`}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Solo el símbolo del logo (el texto de la imagen es ilegible a este tamaño) */}
        <span className="mx-auto block h-[45px] w-[52px] overflow-hidden">
          <img src={logo} alt="Logo Fondo Cafetero" className="h-14 w-auto max-w-none" />
        </span>

        <h3
          id="logout-title"
          className="mt-5 text-balance font-display text-2xl font-semibold leading-tight tracking-tight text-cafe-700 sm:text-[28px]"
        >
          Hasta pronto{firstName && ','}
          {firstName && (
            <>
              {' '}
              <span className="font-normal italic text-cafe-500">{firstName}</span>
            </>
          )}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-neutral-400">Se cerrará su sesión en este equipo.</p>

        <div className="mt-6 flex justify-center">
          {/* Oro suave (#E6C170, no el ámbar #F3A108 de la paleta, que se lee mostaza) con texto café oscuro:
              el blanco sobre dorado no tiene contraste suficiente. El anillo de foco es café porque uno
              dorado no se vería sobre este fondo. */}
          <button
            ref={confirmButtonRef}
            onClick={handleConfirm}
            disabled={loading}
            className="inline-flex min-w-[9.5rem] items-center justify-center gap-2 rounded-xl bg-[#E6C170] px-5 py-2.5 text-sm font-semibold text-cafe-700 shadow-[0_10px_24px_-14px_rgba(224,176,74,0.8)] transition-colors duration-300 hover:bg-[#E0B04A] disabled:cursor-wait disabled:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cafe-700 focus-visible:ring-offset-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Cerrando sesión…
              </>
            ) : (
              <>
                <SignOut className="h-[18px] w-[18px]" weight="bold" />
                Cerrar sesión
              </>
            )}
          </button>
        </div>

        {/* Dato secundario y discreto: va después de la acción para no cortar el flujo del cierre de sesión */}
        {loginTime && (
          <p className="mt-5 text-xs text-neutral-400">
            Inicio <span className="font-medium text-cafe-600">{formatStart(loginTime)}</span>
            <span className="mx-2 text-cafe-200" aria-hidden="true">
              ·
            </span>
            Conectado <span className="font-medium text-cafe-600">{formatDuration(Date.now() - loginTime)}</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default LogoutModal;
